import type { ApiResponse, Tenant } from '@packages/shared-types';

import { apiClient } from '@/services/apiClient';

export interface CreateTenantPayload {
  name: string;
  subdomain?: string;
}

export interface UpdateTenantPayload {
  name?: string;
  subdomain?: string;
  profileImage?: string;
  industry?: string;
  language?: string;
  settings?: Record<string, unknown>;
}

export interface GetTenantsPaginatedParams {
  page: number;
  limit: number;
}

export interface GetTenantsPaginatedResult {
  data: Tenant[];
  total: number;
  page: number;
  limit: number;
}

export async function getTenantsPaginated(
  params: GetTenantsPaginatedParams,
): Promise<GetTenantsPaginatedResult> {
  const { page, limit } = params;
  const response = await apiClient.get<ApiResponse<GetTenantsPaginatedResult>>(
    '/api/tenants',
    { params: { page, limit } },
  );
  const payload = response.data.data;
  return {
    data: payload.data,
    total: payload.total,
    page: payload.page,
    limit: payload.limit,
  };
}

export async function getTenants(): Promise<Tenant[]> {
  const response = await apiClient.get<ApiResponse<Tenant[]>>('/api/tenants');
  return response.data.data;
}

export async function getTenantById(id: string): Promise<Tenant> {
  const response = await apiClient.get<ApiResponse<Tenant>>(`/api/tenants/${id}`);
  return response.data.data;
}

export async function getMyTenant(): Promise<Tenant> {
  const response = await apiClient.get<ApiResponse<Tenant>>('/api/tenants/me');
  return response.data.data;
}

export async function createTenant(
  payload: CreateTenantPayload,
): Promise<Tenant> {
  const response = await apiClient.post<ApiResponse<Tenant>>(
    '/api/tenants',
    payload,
  );
  return response.data.data;
}

export async function updateTenant(
  id: string,
  payload: UpdateTenantPayload,
): Promise<Tenant> {
  const response = await apiClient.put<ApiResponse<Tenant>>(
    `/api/tenants/${id}`,
    payload,
  );
  return response.data.data;
}

export async function uploadTenantImage(
  id: string,
  file: File,
): Promise<Tenant> {
  const formData = new FormData();
  formData.append('image', file);

  const response = await apiClient.post<ApiResponse<Tenant>>(
    `/api/tenants/${id}/upload-image`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );

  return response.data.data;
}

export async function deleteTenant(id: string): Promise<void> {
  await apiClient.delete(`/api/tenants/${id}`);
}
